import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import type * as fhirTypes from "fhir/r4";

interface VoidGenericObservationResponse {
  observation: fhirTypes.Observation;
}

interface VoidGenericObservationVariables {
  observationId: string;
}

export const useVoidGenericObservation = ({
  patientId,
  encounterId,
  group,
  observationCode,
  onSuccess,
}: {
  patientId?: string;
  encounterId?: string;
  group?: string;
  observationCode?: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    VoidGenericObservationResponse,
    VoidGenericObservationVariables
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Observation.VOID_GENERIC({
        patientId,
        encounterId,
      }),
      mutationFn: ({ observationId }) => {
        return axiosInstance.delete(
          `/charting/observation-generic/${observationId}`,
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Observation.DEFAULT({
            patientId,
            encounterId,
          }),
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTER({
            patientId,
            motherEncounterId: encounterId,
          }),
        });

        // Invalidate generic observations queries
        if (patientId && group) {
          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.GenericObservations.DEFAULT(
                patientId,
                group,
              ),
          });
        }

        // Invalidate generic observations history query
        if (patientId && group && observationCode) {
          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.GenericObservations.HISTORY(
                patientId,
                group,
                observationCode,
              ),
          });
        }

        onSuccess?.();
      },
    },
  });
};
